// =============================================================================
// features/sales/contacts/RegisterContactDialog.tsx — Diálogo de registro de
// contato (doc 26 §6.3/§7 "RegisterContactDialog"), reutilizável fora do CRM.
//
// Fluxo em UM diálogo, DOIS clientes HTTP encadeados (doc 26 §22
// "registro de contato em ≤2 cliques"): abrir o diálogo é o 1º clique,
// "Registrar" é o 2º — por trás, o submit cria a tentativa
// (`POST /contact-attempts`) e IMEDIATAMENTE registra o desfecho
// (`POST /contact-attempts/:id/outcome`) na mesma transição visual. Quando o
// desfecho exige retorno (`requiresAppointment`), os campos de data/hora
// aparecem NO MESMO diálogo (doc 26 §6.3) — nenhuma troca de tela.
//
// Erro de rede NUNCA limpa o formulário (doc 26 §7): o `attemptId` já criado
// fica em estado local — se o 2º passo (outcome) falhar e o vendedor clicar
// "Tentar novamente", a tentativa NÃO é recriada (evita duplicar/discar de
// novo), só o outcome é reenviado. `idempotencyKey` fixo por sessão do
// diálogo cobre o caso do 1º passo falhar antes de resolver o attemptId.
//
// A lista de desfechos vem de `useEnabledOutcomes()` (GET /api/sales/outcomes,
// F34-S02) — nunca uma constante local: a organização pode desabilitar
// códigos, e o vendedor só pode escolher entre os habilitados.
//
// `onResolveQueueMember` (F35-S18, opcional): quando este diálogo abre a
// partir da tela de operação de campanha (features/sales/campaigns/operate),
// registrar o desfecho é só metade do trabalho — o membro da fila
// (`campaign_members`) também precisa ser encerrado de verdade
// (`POST .../queue/:memberId/resolve`, F35-S15), senão o cliente volta para
// a fila mesmo já atendido. Esse 3º passo roda ENCADEADO ao 2º (mesmo
// submit), ainda dentro deste diálogo — não depois de fechar — porque um 422
// de `nextActionAt` (data/hora do retorno no passado) precisa aparecer como
// erro no MESMO campo de data que o vendedor acabou de preencher, com o
// formulário ainda aberto. Fechar o diálogo e só então tentar o resolve
// perderia esse contexto. Quando o diálogo é usado fora da operação de
// campanha (ex.: CrmDetailPage), a prop fica ausente e este passo nunca roda
// — comportamento idêntico ao anterior a este slot.
// =============================================================================

import type {
  AttemptOrigin,
  ContactChannel,
  ContactAttemptCreateBody,
  ContactAttemptOutcomeBody,
  SalesOutcomeCode,
} from '@elemento/shared-schemas';
import { CONTACT_CHANNELS } from '@elemento/shared-schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import {
  useCreateContactAttempt,
  useRecordAttemptOutcome,
} from '../../../hooks/sales/useContactAttempts';
import { useEnabledOutcomes } from '../../../hooks/sales/useSalesSettings';
import { ApiError } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { orgLocalToUtcIso } from '../components/dateTimeOrgTz';
import { FormTextarea } from '../components/FormTextarea';
import { OutcomeSelector } from '../components/OutcomeSelector';
import type { OutcomeOption } from '../components/OutcomeSelector';
import { useFocusTrap } from '../components/useFocusTrap';

import { CHANNEL_LABELS } from './labels';

// ---------------------------------------------------------------------------
// Schema dinâmico — as regras de "exige observação"/"exige retorno" vêm dos
// metadados EFETIVOS do outcome escolhido (organização pode customizar via
// `PUT /api/sales/outcomes`), nunca de uma cópia estática no formulário.
// ---------------------------------------------------------------------------

export interface OutcomeMetaLookup {
  [code: string]: { label: string; requiresNote: boolean; requiresAppointment: boolean };
}

export function buildRegisterContactSchema(outcomeMeta: OutcomeMetaLookup) {
  return z
    .object({
      channel: z.string().min(1, 'Selecione o canal'),
      outcomeCode: z.string().min(1, 'Selecione o desfecho'),
      notes: z.string().max(4000, 'Máximo 4000 caracteres').optional(),
      nextStep: z.string().max(2000, 'Máximo 2000 caracteres').optional(),
      nextDate: z.string().optional(),
      nextTime: z.string().optional(),
      nextChannel: z.string().optional(),
      nextReason: z.string().max(500, 'Máximo 500 caracteres').optional(),
    })
    .superRefine((data, ctx) => {
      const meta = outcomeMeta[data.outcomeCode];
      if (!meta) return;

      if (meta.requiresNote && (!data.notes || data.notes.trim().length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['notes'],
          message: `O desfecho "${meta.label}" exige observação para registrar.`,
        });
      }

      if (meta.requiresAppointment && (!data.nextDate || !data.nextTime)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['nextDate'],
          message: `O desfecho "${meta.label}" exige agendar a data e hora do retorno.`,
        });
      }
    });
}

export type RegisterContactFormValues = z.infer<ReturnType<typeof buildRegisterContactSchema>>;

// ---------------------------------------------------------------------------
// Payload de criação (POST /api/sales/contact-attempts) — função pura,
// extraída do `onSubmit` para ser testável sem montar o componente (este
// monorepo não usa @testing-library/react, ver
// features/conversations/components/__tests__/CommercialSection.test.ts).
// `origin` só entra no objeto quando informado — omitir preserva o
// comportamento anterior a este slot (backend cai no default `'manual'`).
// ---------------------------------------------------------------------------

export function buildCreateAttemptBody(input: {
  leadId: string;
  channel: ContactChannel;
  idempotencyKey: string;
  origin?: AttemptOrigin;
}): ContactAttemptCreateBody {
  const { leadId, channel, idempotencyKey, origin } = input;
  return {
    leadId,
    channel,
    idempotencyKey,
    ...(origin ? { origin } : {}),
  };
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RegisterContactDialogProps {
  leadId: string;
  leadName?: string;
  /** Canais bloqueados para este lead (doc 26 §8) — somem do seletor de canal. */
  blockedChannels?: Set<ContactChannel>;
  /**
   * Origem da tentativa (`contact_attempts.origin`, F38-S01). Omitir preserva
   * o comportamento anterior a este slot — o backend cai no default
   * `'manual'` quando `origin` não é enviado (ver
   * `ContactAttemptCreateBodySchema.origin` em `@elemento/shared-schemas`).
   * Cada consumidor deste diálogo deve passar o valor que corresponde ao seu
   * próprio fluxo (live chat → `'livechat'`, operação de campanha →
   * `'campaign_queue'`, CRM → `'manual'` explícito).
   */
  origin?: AttemptOrigin;
  onClose: () => void;
  onRegistered?: (attemptId: string) => void;
  /**
   * Encerramento real do membro de campanha (F35-S15/F35-S18) — presente só
   * quando este diálogo é aberto a partir da tela de operação. Recebe o
   * mesmo `outcomeCode` registrado no passo 2 e o `nextActionAt` (ISO, já
   * convertido do par data/hora local) quando o desfecho exige retorno.
   * Deve rejeitar (throw) em caso de falha — ver header do arquivo.
   */
  onResolveQueueMember?: (
    outcomeCode: SalesOutcomeCode,
    nextActionAt: string | undefined,
  ) => Promise<void>;
}

export function RegisterContactDialog({
  leadId,
  leadName,
  blockedChannels,
  origin,
  onClose,
  onRegistered,
  onResolveQueueMember,
}: RegisterContactDialogProps): React.JSX.Element {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const idempotencyKeyRef = React.useRef<string>(crypto.randomUUID());
  // Guarda o attemptId já criado — um retry após falha do 2º passo (outcome)
  // NUNCA recria a tentativa (evita duplicar/discar de novo, doc 26 §14).
  const [attemptId, setAttemptId] = React.useState<string | null>(null);
  // Guarda o outcome JÁ registrado no servidor — um retry após falha do 3º
  // passo (resolve da fila, F35-S18) NUNCA reenvia o outcome (o servidor
  // rejeitaria com "já registrado"), só tenta o resolve de novo.
  const [registeredOutcomeCode, setRegisteredOutcomeCode] = React.useState<SalesOutcomeCode | null>(
    null,
  );
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const { outcomes, isLoading: outcomesLoading } = useEnabledOutcomes();
  const outcomeOptions: OutcomeOption[] = outcomes.map((o) => ({
    value: o.code,
    label: o.label,
    requiresNote: o.requiresNote,
    requiresAppointment: o.requiresAppointment,
  }));
  const outcomeMeta: OutcomeMetaLookup = Object.fromEntries(
    outcomeOptions.map((o) => [o.value, o]),
  );

  const channelOptions = CONTACT_CHANNELS.filter((c) => !blockedChannels?.has(c)).map((c) => ({
    value: c,
    label: CHANNEL_LABELS[c],
  }));

  const schema = React.useMemo(() => buildRegisterContactSchema(outcomeMeta), [outcomeMeta]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isDirty },
  } = useForm<RegisterContactFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      channel: channelOptions[0]?.value ?? 'phone_manual',
      outcomeCode: '',
      notes: '',
      nextStep: '',
      nextDate: '',
      nextTime: '',
      nextChannel: '',
      nextReason: '',
    },
  });

  const outcomeCode = watch('outcomeCode');
  const selectedMeta = outcomeCode ? outcomeMeta[outcomeCode] : undefined;

  const { createAsync } = useCreateContactAttempt();
  const { recordAsync } = useRecordAttemptOutcome();
  const [isPending, setIsPending] = React.useState(false);

  const handleEscape = React.useCallback(() => {
    if (isDirty && !isPending) return; // guarda de edição — não fecha silenciosamente
    if (!isPending) onClose();
  }, [isDirty, isPending, onClose]);

  useFocusTrap({ containerRef: panelRef, active: true, onEscape: handleEscape });

  async function onSubmit(values: RegisterContactFormValues): Promise<void> {
    setSubmitError(null);
    setIsPending(true);

    // Recalculado a cada submit — inclusive num retry após o 3º passo falhar
    // com 422, quando o vendedor pode ter corrigido a data/hora acima.
    let nextActionAt: string | undefined;
    if (selectedMeta?.requiresAppointment && values.nextDate && values.nextTime) {
      const scheduledAt = orgLocalToUtcIso(values.nextDate, values.nextTime);
      if (!scheduledAt) {
        setSubmitError('Data e hora do retorno inválidas.');
        setIsPending(false);
        return;
      }
      nextActionAt = scheduledAt;
    }

    try {
      let currentAttemptId = attemptId;
      let currentOutcomeCode = registeredOutcomeCode;

      // Passos 1+2 (criar tentativa + registrar desfecho) só rodam uma vez —
      // `registeredOutcomeCode` não nulo significa que já foram bem
      // sucedidos numa tentativa anterior deste mesmo submit (retry após
      // falha só do 3º passo, resolve da fila).
      if (currentOutcomeCode === null) {
        if (!currentAttemptId) {
          const attempt = await createAsync(
            buildCreateAttemptBody({
              leadId,
              // `values.channel` só pode ser um dos valores de
              // `channelOptions` (CONTACT_CHANNELS filtrado) — cast seguro
              // pelo mesmo motivo do OutcomeSelector acima.
              channel: values.channel as ContactChannel,
              idempotencyKey: idempotencyKeyRef.current,
              ...(origin ? { origin } : {}),
            }),
          );
          currentAttemptId = attempt.id;
          setAttemptId(currentAttemptId);
        }

        const body: ContactAttemptOutcomeBody = {
          // Mesma garantia de runtime do cast acima (valor só pode vir do
          // OutcomeSelector, já filtrado pelos desfechos habilitados).
          outcomeCode: outcomeCode as SalesOutcomeCode,
          ...(values.notes?.trim() ? { notes: values.notes.trim() } : {}),
          ...(values.nextStep?.trim() ? { nextStep: values.nextStep.trim() } : {}),
        };

        if (nextActionAt) {
          body.nextAppointment = {
            scheduledAt: nextActionAt,
            // Mesmo motivo do cast acima — vem do <select> de canal do retorno.
            ...(values.nextChannel ? { channel: values.nextChannel as ContactChannel } : {}),
            ...(values.nextReason?.trim() ? { reason: values.nextReason.trim() } : {}),
          };
        }

        const response = await recordAsync({ id: currentAttemptId, body });
        currentOutcomeCode = response.attempt.outcomeCode ?? (outcomeCode as SalesOutcomeCode);
        setRegisteredOutcomeCode(currentOutcomeCode);
      }

      // Passo 3 (F35-S18) — encerra o membro da fila de campanha com o mesmo
      // desfecho. Ausente fora da tela de operação (ver header do arquivo).
      if (onResolveQueueMember) {
        try {
          await onResolveQueueMember(currentOutcomeCode, nextActionAt);
        } catch (resolveError) {
          if (resolveError instanceof ApiError && resolveError.status === 422) {
            setError('nextDate', {
              type: 'server',
              message:
                resolveError.message ||
                'Ajuste a data e a hora do retorno — precisam ser um instante futuro.',
            });
            setSubmitError(
              'O contato foi registrado, mas não foi possível encerrar a reserva com esta data/hora de retorno. Ajuste o campo acima e tente novamente.',
            );
          } else {
            setSubmitError(
              resolveError instanceof Error && resolveError.message
                ? `O contato foi registrado, mas não foi possível encerrar a reserva: ${resolveError.message}`
                : 'O contato foi registrado, mas não foi possível encerrar a reserva. Tente novamente — nada foi perdido.',
            );
          }
          setIsPending(false);
          return;
        }
      }

      setSubmitError(null);
      onRegistered?.(currentAttemptId ?? '');
      onClose();
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Não foi possível registrar o contato. Tente novamente.';
      setSubmitError(message);
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--text) 60%, transparent)' }}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleEscape();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="register-contact-title"
        className={cn(
          'w-full max-w-lg rounded-md flex flex-col overflow-hidden',
          'max-h-[min(90vh,780px)]',
        )}
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--border)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-start justify-between px-6 py-5 shrink-0"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <div className="flex flex-col gap-1">
            <h2
              id="register-contact-title"
              className="font-display font-bold text-ink"
              style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
            >
              Registrar contato
            </h2>
            {leadName && <p className="font-sans text-sm text-ink-3">{leadName}</p>}
          </div>

          <button
            type="button"
            onClick={handleEscape}
            disabled={isPending}
            aria-label="Fechar diálogo de registro de contato"
            className={cn(
              'p-2 rounded-sm text-ink-3 hover:text-ink hover:bg-surface-hover',
              'transition-colors duration-fast disabled:opacity-50 disabled:cursor-not-allowed',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40',
            )}
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path d="M3 3l10 10M13 3L3 13" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <form
          onSubmit={(e) => {
            void handleSubmit(onSubmit)(e);
          }}
          noValidate
          className="flex flex-col gap-5 px-6 py-5 overflow-y-auto"
        >
          <Select
            id="register-contact-channel"
            label="Canal"
            required
            options={channelOptions}
            error={errors.channel?.message}
            disabled={isPending || Boolean(attemptId)}
            hint={
              attemptId
                ? 'O canal não muda mais — a tentativa já foi registrada.'
                : channelOptions.length < CONTACT_CHANNELS.length
                  ? 'Canais bloqueados para este lead não aparecem aqui.'
                  : undefined
            }
            {...register('channel')}
          />

          <OutcomeSelector
            id="register-contact-outcome"
            required
            // O valor de `outcomeCode` só pode vir do <select> do próprio
            // OutcomeSelector, populado exclusivamente com `outcomeOptions`
            // (SalesOutcomeCode válidos vindos de GET /api/sales/outcomes) —
            // o cast é seguro em runtime, o schema Zod (`z.string()`) só não
            // consegue expressar essa restrição no tipo do formulário.
            value={(outcomeCode as SalesOutcomeCode | '' | undefined) ?? ''}
            onChange={(value) =>
              setValue('outcomeCode', value, { shouldValidate: true, shouldDirty: true })
            }
            options={outcomeOptions}
            error={errors.outcomeCode?.message}
            disabled={isPending || outcomesLoading}
          />

          <FormTextarea
            id="register-contact-notes"
            label="Observação"
            required={selectedMeta?.requiresNote ?? false}
            placeholder="Registre o contexto do contato"
            disabled={isPending}
            error={errors.notes?.message}
            {...register('notes')}
          />

          <FormTextarea
            id="register-contact-next-step"
            label="Próximo passo"
            placeholder="Combinado com o cliente (opcional)"
            disabled={isPending}
            error={errors.nextStep?.message}
            {...register('nextStep')}
          />

          {selectedMeta?.requiresAppointment && (
            <div
              className="flex flex-col gap-4 rounded-sm px-4 py-4"
              style={{ background: 'var(--bg-elev-2)', boxShadow: 'var(--elev-1)' }}
            >
              <p className="font-sans text-xs font-bold uppercase tracking-[0.08em] text-ink-3">
                Novo retorno (obrigatório)
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  id="register-contact-next-date"
                  label="Data"
                  type="date"
                  required
                  disabled={isPending}
                  error={errors.nextDate?.message}
                  {...register('nextDate')}
                />
                <Input
                  id="register-contact-next-time"
                  label="Hora"
                  type="time"
                  required
                  disabled={isPending}
                  {...register('nextTime')}
                />
              </div>
              <Select
                id="register-contact-next-channel"
                label="Canal do retorno"
                placeholder="Mesmo canal do contato"
                options={CONTACT_CHANNELS.filter((c) => !blockedChannels?.has(c)).map((c) => ({
                  value: c,
                  label: CHANNEL_LABELS[c],
                }))}
                disabled={isPending}
                {...register('nextChannel')}
              />
              <Input
                id="register-contact-next-reason"
                label="Motivo do retorno"
                placeholder="Opcional"
                disabled={isPending}
                {...register('nextReason')}
              />
            </div>
          )}

          {submitError && (
            <div
              role="alert"
              className="rounded-sm px-4 py-3 border-l-[3px]"
              style={{
                borderColor: 'var(--danger)',
                background: 'var(--danger-bg)',
                boxShadow: 'var(--elev-1)',
              }}
            >
              <p className="font-sans font-semibold text-sm text-danger">Erro ao registrar</p>
              <p className="font-sans text-xs text-ink-2 mt-0.5">{submitError}</p>
              <p className="font-sans text-xs text-ink-3 mt-1">
                Nada do que você preencheu foi perdido — pode tentar novamente.
              </p>
            </div>
          )}

          <div
            className="flex flex-col gap-2.5 pt-1 mt-1 shrink-0"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <Button
              type="submit"
              variant="primary"
              disabled={isPending || outcomesLoading}
              className="w-full justify-center"
            >
              {isPending ? 'Registrando…' : submitError ? 'Tentar novamente' : 'Registrar contato'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isPending}
              className="w-full justify-center"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
